import { getOfferCapabilities } from "../../common/offerCapabilities";
import React, { useEffect, useState } from "react";
import { Box, Header, Button, Heading, Text, Image } from "grommet";
import { Trash, Edit, Add, Download } from "grommet-icons";
import { presentToastSuccess, presentToastErrorContent } from "@shared/Toast";
import { AddRoomType } from "./";
import { RoomTypeService, PmsService, PhotoService } from "../../services";
import { useFetchHotel, useFetchRoomTypes } from "./redux/hooks";
import { presentAlertPrompt } from "@shared/Alert";
import { presentLoading, dismissLoading } from "@shared/Loading";
import { SortableList } from "@shared";
import noPhoto from "../../images/nophoto.jpg";
import { pmsThatCanImportRoomTypes } from "@common/features";
import { useTranslation } from "react-i18next";

export default function SettingsRoomTypes(props) {
  const { t } = useTranslation();
  const { roomTypes } = useFetchRoomTypes();
  const { hotel } = useFetchHotel();
  const [list, setList] = useState([]);
  const [roomTypeId, setRoomTypeId] = useState(0);
  const [openEdit, setOpenEdit] = useState(false);

  useEffect(() => {
    setList(roomTypes || []);
  }, [roomTypes]);

  const SortableItem = ({ value }) => (
    <SortableList.Item id={value._id}>
      <Box direction="row" gap="medium" justify="between">
        <Box alignContent="center" className="room-types-column">
          <SortableList.DragHandle />
        </Box>
        <Box
          direction="row"
          gap="medium"
          width="400px"
          className="room-types-column"
          align="start"
        >
          <Box height="70px" width="70px">
            <Image
              height="70px"
              width="70px"
              fit="cover"
              src={PhotoService.getURL(value.photo) || noPhoto}
              className="room-types-image"
              onClick={() => handleEdit(value._id)}
            />
          </Box>
          <Box gap="small" width="330px">
            <Text weight="bold">{value.name}</Text>
            <Text truncate color="dark-6" style={{ fontStyle: "italic" }}>
              {value.description}
            </Text>
          </Box>
        </Box>
        {getOfferCapabilities(hotel).upsells && <>
        <Box className="room-types-column" width="100px" align="center">
          {value.order === 0 ? (
            "-"
          ) : (
            <Box align="center">
              <Text size="xsmall">{t("settingsRoomTypes.upgrade")}</Text>
              <Text>${Math.round(value.upgradePrice * 100 || 0) / 100}</Text>
            </Box>
          )}
        </Box>
        <Box className="room-types-column" width="90px" align="center">
          <Text size="xsmall" style={{ textAlign: "center" }}>
            {t("settingsRoomTypes.earlyCheckin")}
          </Text>
          <Text>${Math.round(value.earlyCheckinPrice * 100 || 0) / 100}</Text>
        </Box>
        <Box className="room-types-column" width="90px" align="center">
          <Text size="xsmall" style={{ textAlign: "center" }}>
            {t("settingsRoomTypes.lateCheckout")}
          </Text>
          <Text>${Math.round(value.lateCheckoutPrice * 100 || 0) / 100}</Text>
        </Box>
        </>}
        <Box direction="row" gap="medium" className="room-types-column">
          <Button
            icon={<Edit size="small" />}
            size="small"
            label={t("settingsRoomTypes.edit")}
            alignSelf="center"
            primary
            color="status-warning"
            onClick={() => handleEdit(value._id)}
          />
          <Button
            icon={<Trash size="small" />}
            size="small"
            label={t("settingsRoomTypes.remove")}
            alignSelf="center"
            primary
            color="status-critical"
            onClick={() => handleRemoveRoomType(value._id)}
          />
        </Box>
      </Box>
    </SortableList.Item>
  );

  const onRefresh = () => {
    RoomTypeService.getRoomTypes().then(
      (response) => {
        setList(response.data);
      },
      (error) => {
        presentToastErrorContent(error);
      },
    );
  };

  const handleEditModalClose = (refresh) => {
    if (refresh) onRefresh();

    setOpenEdit(false);
  };

  const handleRemoveRoomType = (roomTypeId) => {
    presentAlertPrompt({
      title: t("settingsRoomTypes.deleteConfirmTitle"),
      message: t("settingsRoomTypes.deleteConfirmMessage"),
      onOK: () => {
        removeRoomType(roomTypeId);
      },
      buttonOKText: t("settingsRoomTypes.deleteConfirmButton"),
      buttonOKColor: "status-critical",
    });
  };

  const removeRoomType = (roomTypeId) => {
    RoomTypeService.deleteRoomType(roomTypeId).then(
      (response) => {
        onRefresh();
        presentToastSuccess("Room type successfully removed.");
      },
      (error) => {
        presentToastErrorContent(error);
      },
    );
  };

  const handleEdit = (roomTypeId) => {
    setRoomTypeId(roomTypeId);
    setOpenEdit(true);
  };

  const handleAdd = () => {
    setRoomTypeId(0);
    setOpenEdit(true);
  };

  const handleImport = () => {
    presentLoading();
    PmsService.importRoomTypes().then(
      (response) => {
        onRefresh();
        dismissLoading();
      },
      (error) => {
        dismissLoading();
      },
    );
  };

  const onSortEnd = (newSortedList) => {
    setList(newSortedList);
    const oldList = list;
    const listIds = newSortedList.map((x) => x._id);
    RoomTypeService.updateOrder(listIds).then(
      (response) => {
        // onRefresh();
      },
      (error) => {
        setList(oldList);
        presentToastErrorContent(error);
      },
    );
  };

  return (
    <Box
      gap="medium"
      justify="start"
      className="settings-room-types"
      flex="grow"
    >
      <Heading alignSelf="start" level="2">
        {t("settingsRoomTypes.title")}
      </Heading>
      <Header
        align="center"
        direction="row"
        justify="between"
        gap="medium"
        fill="horizontal"
      >
        {pmsThatCanImportRoomTypes.includes(hotel.pmsSlug) && (
          <Button
            icon={<Download size="small" />}
            label={t("settingsRoomTypes.importFromPms")}
            primary
            color="status-warning"
            onClick={handleImport}
          />
        )}
        <Button
          primary
          icon={<Add size="small" />}
          size="small"
          label={t("settingsRoomTypes.add")}
          onClick={handleAdd}
        />
        {openEdit && (
          <AddRoomType roomTypeId={roomTypeId} onClose={handleEditModalClose} />
        )}
      </Header>

      <SortableList
        items={list.map((x) => {
          return { ...x, id: x._id };
        })}
        onChange={onSortEnd}
        renderItem={(item) => <SortableItem value={item} />}
      />
    </Box>
  );
}

SettingsRoomTypes.propTypes = {};
SettingsRoomTypes.defaultProps = {};
